import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, UserPlus, FlaskConical, Settings, LogOut, User, Info, Mail, ChevronDown, Activity, Menu, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePatient } from '../contexts/PatientContext';

function useProfilePhoto(username) {
  const [photo, setPhoto] = useState(() =>
    username ? localStorage.getItem(`profile_photo_${username}`) : null
  );

  useEffect(() => {
    if (!username) return;
    setPhoto(localStorage.getItem(`profile_photo_${username}`));

    const handleStorage = (e) => {
      if (e.key === `profile_photo_${username}`) setPhoto(e.newValue);
    };
    window.addEventListener('storage', handleStorage);

    const interval = setInterval(() => {
      setPhoto(localStorage.getItem(`profile_photo_${username}`) || null);
    }, 1000);

    return () => {
      window.removeEventListener('storage', handleStorage);
      clearInterval(interval);
    };
  }, [username]);

  return photo;
}

export default function Layout({ children }) {
  const { username, logout } = useAuth();
  const { setPatient } = usePatient();
  const location = useLocation();
  const navigate = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const settingsRef = useRef(null);
  const photo = useProfilePhoto(username);

  useEffect(() => {
    const handleClick = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) setSettingsOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    setPatient(null);
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/', icon: Home, label: 'Home' },
    { path: '/patient', icon: UserPlus, label: 'Patient' },
    { path: '/analysis', icon: FlaskConical, label: 'Analysis' },
  ];

  const isActive = (path) => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const NavAvatar = ({ size = 26 }) => (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0, overflow: 'hidden',
      background: 'linear-gradient(135deg, var(--blue), var(--teal))',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      border: photo ? '1.5px solid rgba(255,255,255,0.3)' : 'none',
    }}>
      {photo
        ? <img src={photo} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : <User size={Math.round(size * 0.5)} color="#fff" />
      }
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--gray-50)', display: 'flex', flexDirection: 'column' }}>
      {/* Top Header */}
      <header style={{
        background: 'var(--navy)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: 60,
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: '0 2px 16px rgba(0,0,0,0.2)',
      }}>
        {/* Logo */}
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', flexShrink: 0 }}>
          <div style={{
            width: 34, height: 34, borderRadius: 10,
            background: 'linear-gradient(135deg, var(--blue) 0%, var(--teal) 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Activity size={18} color="#fff" />
          </div>
          <div>
            <span style={{ fontSize: 17, fontWeight: 700, color: '#fff', fontFamily: "'DM Serif Display', serif", letterSpacing: '0.2px' }}>Cytometrix</span>
            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', display: 'block', marginTop: -3, letterSpacing: '1px', textTransform: 'uppercase' }}>Urinalysis Platform</span>
          </div>
        </Link>

        {/* Center Nav — hidden on mobile */}
        <nav className="desktop-nav" style={{ display: 'flex', gap: 4 }}>
          {navItems.map(({ path, icon: Icon, label }) => (
            <Link key={path} to={path} style={{
              display: 'flex', alignItems: 'center', gap: 7, padding: '7px 16px',
              borderRadius: 10, textDecoration: 'none', fontSize: 13.5, fontWeight: 600,
              color: isActive(path) ? '#fff' : 'rgba(255,255,255,0.55)',
              background: isActive(path) ? 'rgba(255,255,255,0.12)' : 'transparent',
              transition: 'all 0.2s',
            }}
              onMouseEnter={e => { if (!isActive(path)) e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; }}
              onMouseLeave={e => { if (!isActive(path)) e.currentTarget.style.color = 'rgba(255,255,255,0.55)'; }}
            >
              <Icon size={15} />
              {label}
            </Link>
          ))}
        </nav>

        {/* Right side: User Menu (desktop) + Hamburger (mobile) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* User Menu — hidden on mobile */}
          <div ref={settingsRef} className="desktop-nav" style={{ position: 'relative' }}>
            <button onClick={() => setSettingsOpen(!settingsOpen)} style={{
              display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, padding: '7px 14px',
              cursor: 'pointer', color: '#fff', fontSize: 13.5, fontWeight: 600, fontFamily: "'DM Sans', sans-serif",
              transition: 'all 0.2s',
            }}>
              <NavAvatar size={26} />
              <span>{username || 'User'}</span>
              <ChevronDown size={13} style={{ opacity: 0.6 }} />
            </button>

            {settingsOpen && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 8px)', right: 0, background: '#fff',
                borderRadius: 12, boxShadow: 'var(--shadow-lg)', border: '1px solid var(--gray-200)',
                minWidth: 200, overflow: 'hidden', animation: 'dropIn 0.15s ease',
                zIndex: 200,
              }}>
                <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--gray-100)', background: 'var(--gray-50)', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <NavAvatar size={40} />
                  <div>
                    <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>{username}</p>
                    <p style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 2 }}>Cytometrix Account</p>
                  </div>
                </div>

                {[
                  { path: '/account', icon: User, label: 'Edit Profile' },
                  { path: '/about', icon: Info, label: 'About Us' },
                  { path: '/contact', icon: Mail, label: 'Contact Us' },
                ].map(({ path, icon: Icon, label }) => (
                  <Link key={path} to={path} onClick={() => setSettingsOpen(false)} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px',
                    textDecoration: 'none', color: 'var(--gray-700)', fontSize: 14, fontWeight: 500,
                    transition: 'background 0.15s',
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--gray-50)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <Icon size={15} color="var(--gray-400)" />{label}
                  </Link>
                ))}

                <div style={{ borderTop: '1px solid var(--gray-100)' }}>
                  <button onClick={handleLogout} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px', width: '100%',
                    background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', fontSize: 14, fontWeight: 500,
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--red-soft)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <LogOut size={15} />Logout
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Hamburger — visible on mobile only */}
          <button
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{
              display: 'none', /* shown via CSS */
              alignItems: 'center', justifyContent: 'center',
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 10, width: 40, height: 40,
              cursor: 'pointer', color: '#fff',
            }}
          >
            {mobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </header>

      {/* Mobile Dropdown Menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu" style={{
          background: 'var(--navy)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          padding: '12px 16px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          position: 'sticky',
          top: 60,
          zIndex: 99,
          boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
        }}>
          {/* User info row */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 12px', marginBottom: 6,
            background: 'rgba(255,255,255,0.06)', borderRadius: 10,
          }}>
            <NavAvatar size={32} />
            <div>
              <p style={{ fontSize: 13, fontWeight: 700, color: '#fff', margin: 0 }}>{username}</p>
              <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', margin: 0, textTransform: 'uppercase', letterSpacing: '0.8px' }}>Cytometrix Account</p>
            </div>
          </div>

          {/* Nav links */}
          {navItems.map(({ path, icon: Icon, label }) => (
            <Link key={path} to={path} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
              borderRadius: 10, textDecoration: 'none', fontSize: 14, fontWeight: 600,
              color: isActive(path) ? '#fff' : 'rgba(255,255,255,0.6)',
              background: isActive(path) ? 'rgba(255,255,255,0.12)' : 'transparent',
            }}>
              <Icon size={16} />{label}
            </Link>
          ))}

          {/* Divider */}
          <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '6px 0' }} />

          {/* Settings links */}
          {[
            { path: '/account', icon: User, label: 'Edit Profile' },
            { path: '/about', icon: Info, label: 'About Us' },
            { path: '/contact', icon: Mail, label: 'Contact Us' },
          ].map(({ path, icon: Icon, label }) => (
            <Link key={path} to={path} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
              borderRadius: 10, textDecoration: 'none', fontSize: 14, fontWeight: 500,
              color: 'rgba(255,255,255,0.6)',
            }}>
              <Icon size={16} />{label}
            </Link>
          ))}

          {/* Logout */}
          <button onClick={handleLogout} style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
            borderRadius: 10, background: 'rgba(239,68,68,0.12)', border: 'none',
            cursor: 'pointer', color: '#f87171', fontSize: 14, fontWeight: 500,
            fontFamily: "'DM Sans', sans-serif", width: '100%', marginTop: 2,
          }}>
            <LogOut size={16} />Logout
          </button>
        </div>
      )}

      {/* Page Content */}
      <main style={{ flex: 1, padding: '32px 24px', maxWidth: 1140, margin: '0 auto', width: '100%' }}>
        {children}
      </main>

      <style>{`
        @keyframes dropIn { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }

        /* Desktop nav visible by default */
        .desktop-nav { display: flex !important; }
        .mobile-menu-btn { display: none !important; }
        .mobile-menu { display: flex !important; }

        /* Mobile breakpoint */
        @media (max-width: 640px) {
          .desktop-nav { display: none !important; }
          .mobile-menu-btn { display: flex !important; }

          main {
            padding: 20px 16px !important;
          }
        }
      `}</style>
    </div>
  );
}
